import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Input,
  Statistic,
  Select,
  Tag,
  DatePicker,
  Grid,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  DownloadOutlined,
  SyncOutlined,
  CloudSyncOutlined,
  UnorderedListOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { usePayments } from "../../../hooks/finance/usePayments";
import PaymentsTable from "../../../components/finance/payments/PaymentsTable";
import PaymentDetailsDrawer from "../../../components/finance/payments/PaymentDetailsDrawer";
import PaymentValidationModal from "../../../components/finance/payments/PaymentValidationModal";
import SchoolPaySyncModal from "../../../components/finance/payments/SchoolPaySyncModal";
import CreateManualPaymentModal from "../../../components/finance/payments/CreateManualPaymentModal";
import VoidPaymentModal from "../../../components/finance/payments/VoidPaymentModal";
import useValuesStore from "../../../store/values-store";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { PAYMENT_PURPOSE_OPTIONS } from "../../../utils/paymentPurpose";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;
const ALLOCATION_BATCH_SIZE = 20;

const PaymentsManagement = () => {
  const valuesStore = useValuesStore();
  const screens = useBreakpoint();
  const compactActions = !screens.lg;
  const {
    payments,
    loading,
    pagination,
    summary,
    setPagination,
    fetchPayments,
    allocateMissingPayments,
    voidPayment,
    syncSchoolPayTransactions,
  } = usePayments();

  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [validationModalVisible, setValidationModalVisible] = useState(false);
  const [schoolPaySyncModalVisible, setSchoolPaySyncModalVisible] =
    useState(false);
  const [createPaymentModalVisible, setCreatePaymentModalVisible] =
    useState(false);
  const [voidPaymentModalVisible, setVoidPaymentModalVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [schoolPaySyncResult, setSchoolPaySyncResult] = useState(null);
  const canVoidPayments = useMemo(
    () =>
      (valuesStore.permissions || []).some(
        (permission) => permission.name === "finance_payments_void",
      ),
    [valuesStore.permissions],
  );

  const [filters, setFilters] = useState({
    searchText: "",
    status: null,
    validationStatus: null,
    paymentMethod: null,
    paymentChannel: null,
    purpose: null,
    dateRange: null,
  });

  const serverFilters = useMemo(() => {
    const nextFilters = {};
    if (filters.searchText.trim())
      nextFilters.search = filters.searchText.trim();
    if (filters.status) nextFilters.status = filters.status;
    if (filters.validationStatus)
      nextFilters.validation_status = filters.validationStatus;
    if (filters.paymentMethod)
      nextFilters.payment_method = filters.paymentMethod;
    if (filters.paymentChannel)
      nextFilters.payment_channel = filters.paymentChannel;
    if (filters.purpose) nextFilters.purpose = filters.purpose;
    if (filters.dateRange && filters.dateRange.length === 2) {
      nextFilters.date_from = filters.dateRange[0].format("YYYY-MM-DD");
      nextFilters.date_to = filters.dateRange[1].format("YYYY-MM-DD");
    }
    return nextFilters;
  }, [
    filters.searchText,
    filters.status,
    filters.validationStatus,
    filters.paymentMethod,
    filters.paymentChannel,
    filters.purpose,
    filters.dateRange,
  ]);

  useEffect(() => {
    const timer = setTimeout(
      () =>
        fetchPayments({
          page: 1,
          pageSize: pagination.pageSize,
          filters: serverFilters,
        }),
      filters.searchText ? 300 : 0,
    );
    return () => clearTimeout(timer);
    // Pagination changes are handled explicitly by the table callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPayments, serverFilters]);

  const refreshPayments = () =>
    fetchPayments({
      page: pagination.current,
      pageSize: pagination.pageSize,
      filters: serverFilters,
    });

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setDetailsDrawerVisible(true);
  };

  const handleValidate = (payment) => {
    setSelectedPayment(payment);
    setValidationModalVisible(true);
  };

  const handleVoid = (payment) => {
    setSelectedPayment(payment);
    setVoidPaymentModalVisible(true);
  };

  const handleVoidConfirm = async (reason) => {
    const result = await voidPayment(selectedPayment.paymentId, reason);
    if (!result) return;
    setVoidPaymentModalVisible(false);
    setDetailsDrawerVisible(false);
    setSelectedPayment(null);
    refreshPayments();
  };

  const handleAllocateMissing = async (paymentOrIds) => {
    const paymentIds = Array.isArray(paymentOrIds)
      ? paymentOrIds
      : [paymentOrIds.paymentId];

    if (!paymentIds.length) return;

    setAllocating(true);
    try {
      const summary = {
        allocated: 0,
        alreadyAllocated: 0,
        skipped: 0,
        notFound: 0,
      };

      for (
        let index = 0;
        index < paymentIds.length;
        index += ALLOCATION_BATCH_SIZE
      ) {
        const batch = paymentIds.slice(index, index + ALLOCATION_BATCH_SIZE);
        const result = await allocateMissingPayments(batch, {
          silent: true,
          refresh: false,
        });
        if (!result) {
          message.error(
            `Allocation stopped at records ${index + 1}-${index + batch.length}`,
          );
          break;
        }
        summary.allocated += result.allocated || 0;
        summary.alreadyAllocated += result.alreadyAllocated || 0;
        summary.skipped += result.skipped || 0;
        summary.notFound += result.notFound || 0;
      }

      message.success(
        `Allocation run complete: ${summary.allocated} applied, ${summary.alreadyAllocated} already allocated, ${summary.skipped} skipped`,
      );
      setSelectedPaymentIds([]);
      refreshPayments();
    } finally {
      setAllocating(false);
    }
  };

  const handleModalSuccess = () => {
    setValidationModalVisible(false);
    setDetailsDrawerVisible(false);
    setCreatePaymentModalVisible(false);
    setSelectedPayment(null);
    refreshPayments();
  };

  const handleModalCancel = () => {
    setValidationModalVisible(false);
    setSelectedPayment(null);
  };

  const handleSchoolPaySync = async (syncData) => {
    setSyncing(true);
    setSchoolPaySyncResult(null);
    try {
      const result = await syncSchoolPayTransactions(syncData);
      if (result) {
        setSchoolPaySyncResult(result);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleSchoolPaySyncCancel = () => {
    setSchoolPaySyncModalVisible(false);
    setSchoolPaySyncResult(null);
  };

  const handleTablePaginationChange = (page, pageSize) => {
    const nextPage = pageSize !== pagination.pageSize ? 1 : page;
    setPagination((currentPagination) => ({
      ...currentPagination,
      current: nextPage,
      pageSize,
    }));
    fetchPayments({ page: nextPage, pageSize, filters: serverFilters });
  };

  const stats = summary;

  const exportToExcel = () => {
    const exportData = payments.map((payment) => {
      const verifiedStudentName = payment.studentName || payment.student?.name;
      const reportedStudentName = payment.reportedStudentName;

      return {
        "Payment Reference": payment.paymentReference,
        "Student Name": verifiedStudentName || reportedStudentName || "-",
        "Student Name Source": verifiedStudentName
          ? "Verified"
          : reportedStudentName
            ? "Unmatched · SurePay provided"
            : "-",
        "SurePay Student Code": payment.surePayStudentCode || "-",
        "SPC/TPC": payment.spc || payment.tpc || "-",
        Amount: parseFloat(payment.amount),
        "Payment Date": payment.paymentDate
          ? dayjs(payment.paymentDate).format("YYYY-MM-DD HH:mm")
          : "-",
        "Payment Method": payment.paymentMethod,
        "Payment Channel": payment.paymentChannel || "-",
        Status: payment.status,
        "Validation Status": payment.validationStatus || "-",
        "Voided By": payment.voidedBy || "-",
        "Void Reason": payment.voidReason || "-",
        "Created At": payment.createdAt
          ? dayjs(payment.createdAt).format("YYYY-MM-DD HH:mm")
          : "-",
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payments");

    const fileName = `payments_${dayjs().format("YYYY-MM-DD_HHmmss")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Payments"
                value={stats.total}
                valueStyle={{ color: "#1890ff" }}
                prefix={<UnorderedListOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Valid Payments"
                value={stats.valid}
                valueStyle={{ color: "#52c41a" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Pending Validation"
                value={stats.pending}
                valueStyle={{ color: "#faad14" }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="In Suspense"
                value={stats.suspense}
                valueStyle={{ color: "#ff4d4f" }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24}>
            <Card>
              <Statistic
                title="Total Valid Amount"
                value={stats.totalAmount}
                precision={2}
                valueStyle={{ color: "#52c41a", fontSize: "24px" }}
              />
            </Card>
          </Col>
        </Row>

        <Card
          title={
            <Space>
              <UnorderedListOutlined />
              <span>Payment Records</span>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: 12,
                width: "100%",
              }}
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreatePaymentModalVisible(true)}
              >
                {compactActions ? "Manual Payment" : "Create Manual Payment"}
              </Button>
              <Button
                icon={<CloudSyncOutlined />}
                onClick={() => setSchoolPaySyncModalVisible(true)}
                loading={syncing}
              >
                Sync SchoolPay
              </Button>
              <Button
                icon={<SyncOutlined />}
                onClick={refreshPayments}
                loading={loading}
              >
                Refresh
              </Button>
              <Button
                icon={<CheckCircleOutlined />}
                onClick={() => handleAllocateMissing(selectedPaymentIds)}
                loading={allocating}
                disabled={selectedPaymentIds.length === 0}
              >
                {compactActions
                  ? `Allocate (${selectedPaymentIds.length})`
                  : `Apply Missing Allocations (${selectedPaymentIds.length})`}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportToExcel}
                disabled={payments.length === 0}
              >
                Export
              </Button>
            </div>

            {/* Filters */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={8} xl={6}>
                <Input
                  placeholder="Search by reference, student name, or SPC..."
                  prefix={<SearchOutlined />}
                  value={filters.searchText}
                  onChange={(e) =>
                    setFilters({ ...filters, searchText: e.target.value })
                  }
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={12} lg={8} xl={5}>
                <Select
                  placeholder="Filter by Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.status}
                  onChange={(value) =>
                    setFilters({ ...filters, status: value })
                  }
                >
                  <Option value="Valid">Valid</Option>
                  <Option value="Pending Validation">Pending Validation</Option>
                  <Option value="Suspense">Suspense</Option>
                  <Option value="Voided">Voided</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8} xl={6}>
                <Select
                  placeholder="Filter by Validation Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.validationStatus}
                  onChange={(value) =>
                    setFilters({ ...filters, validationStatus: value })
                  }
                >
                  <Option value="Auto Validated">Auto Validated</Option>
                  <Option value="Manual Review">Manual Review</Option>
                  <Option value="Rejected">Rejected</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8} xl={5}>
                <Select
                  placeholder="Filter by Payment Method"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.paymentMethod}
                  onChange={(value) =>
                    setFilters({ ...filters, paymentMethod: value })
                  }
                >
                  <Option value="Bank Transfer">Bank Transfer</Option>
                  <Option value="Mobile Money">Mobile Money</Option>
                  <Option value="Card">Card</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8} xl={5}>
                <Select
                  placeholder="Filter by Channel"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.paymentChannel}
                  onChange={(value) =>
                    setFilters({ ...filters, paymentChannel: value })
                  }
                >
                  <Option value="SUREPAY">SurePay</Option>
                  <Option value="SCHOOLPAY">SchoolPay</Option>
                  <Option value="PESAPAL">PesaPal</Option>
                  <Option value="Manual">Manual</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8} xl={6}>
                <Select
                  placeholder="Filter by Purpose"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.purpose}
                  onChange={(value) =>
                    setFilters({ ...filters, purpose: value })
                  }
                >
                  {PAYMENT_PURPOSE_OPTIONS.map(({ label, value }) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={12} lg={8} xl={6}>
                <RangePicker
                  style={{ width: "100%" }}
                  value={filters.dateRange}
                  onChange={(dates) =>
                    setFilters({ ...filters, dateRange: dates })
                  }
                  format="YYYY-MM-DD"
                />
              </Col>
            </Row>

            {(filters.searchText ||
              filters.status ||
              filters.validationStatus ||
              filters.paymentMethod ||
              filters.paymentChannel ||
              filters.purpose ||
              filters.dateRange) && (
              <div>
                <Tag color="blue">
                  Showing {payments.length} of {pagination.total} payments
                </Tag>
              </div>
            )}

            <PaymentsTable
              payments={payments}
              loading={loading || allocating}
              onViewDetails={handleViewDetails}
              onValidate={handleValidate}
              onVoid={handleVoid}
              canVoid={canVoidPayments}
              onAllocateMissing={handleAllocateMissing}
              selectedRowKeys={selectedPaymentIds}
              onSelectedRowKeysChange={setSelectedPaymentIds}
              pagination={pagination}
              onPaginationChange={handleTablePaginationChange}
            />
          </Space>
        </Card>
      </Space>

      <PaymentDetailsDrawer
        visible={detailsDrawerVisible}
        onClose={() => setDetailsDrawerVisible(false)}
        paymentId={selectedPayment?.paymentId}
        onValidate={handleValidate}
        onVoid={handleVoid}
        canVoid={canVoidPayments}
      />

      <PaymentValidationModal
        visible={validationModalVisible}
        onCancel={handleModalCancel}
        onSuccess={handleModalSuccess}
        payment={selectedPayment}
      />

      <SchoolPaySyncModal
        visible={schoolPaySyncModalVisible}
        onCancel={handleSchoolPaySyncCancel}
        onSync={handleSchoolPaySync}
        loading={syncing}
        result={schoolPaySyncResult}
      />

      <CreateManualPaymentModal
        visible={createPaymentModalVisible}
        onCancel={() => setCreatePaymentModalVisible(false)}
        onSuccess={handleModalSuccess}
      />

      <VoidPaymentModal
        visible={voidPaymentModalVisible}
        payment={selectedPayment}
        onCancel={() => {
          setVoidPaymentModalVisible(false);
          setSelectedPayment(null);
        }}
        onConfirm={handleVoidConfirm}
      />
    </>
  );
};

export default PaymentsManagement;
